import { useCallback, useEffect, useRef } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api } from "@/shared/api/client";
import { authApi } from "@/shared/api/auth";
import { useAuth } from "@/shared/auth/store";
import { currentLanguage, type Language } from "@/shared/i18n";
import {
  ALERT_LANGUAGES,
  buildAlertPhrase,
  SPEECH_LOCALE,
  toAlertLang,
  type AlertLang,
  type Level,
} from "@/shared/i18n/alertPhrases";
import { isNativeTTS, nativeSpeak, nativeStopSpeaking } from "@/shared/native/tts";

/**
 * useAlertTTS — spoken alert announcements in 8 languages (협약 6종
 * ko · en · vi · mn · zh · ja + 추가 2종 th · id) via the Web Speech API,
 * with a server-side edge-tts fallback.
 *
 * Playback strategy (per the P4-B spec):
 *   1. Web Speech API (`speechSynthesis`) using a locally-installed voice for
 *      the target language.
 *   2. If no local voice exists for that language (common for vi/th/id on
 *      desktop, and locked-down kiosks) → fall back to the backend
 *      `GET /api/v1/tts/speak` proxy (edge-tts → cached MP3). If that is also
 *      unavailable (503), the alarm degrades to visual-only — never crashes.
 *
 * 네이티브(Capacitor 안드로이드/iOS)에서는 **1단계가 OS TTS 엔진**으로 바뀐다:
 * 안드로이드 WebView 의 `speechSynthesis.getVoices()` 가 빈 배열이라 Web Speech 로는
 * 로컬 음성을 하나도 찾지 못하고, 결국 모든 언어가 서버 edge-tts 를 타야만 소리가
 * 난다 — 데이터가 끊기면 음성 경보가 통째로 무음이 된다. 그래서 네이티브에서는
 *   네이티브 TTS(`@capacitor-community/text-to-speech`) → 서버 edge-tts MP3
 * 순서로 발화한다(`shared/native/tts.ts`). 엔진에 언어팩이 없는 언어는 억지로
 * 넘기지 않고 곧장 서버 폴백으로 보낸다. **웹 경로는 그대로다.**
 *
 * Language: follows the app UI language (`currentLanguage()`) unless the user
 * sets an explicit voice override (`langOverride`). Both the passive announcer
 * (AppShell) and the critical-alarm overlay (AlarmCenter) read this hook.
 *
 * Broadcast languages: automatic alarm announcements (`announce`/`announceOne`)
 * are spoken in **every language the operator selected in Settings**
 * (`broadcastLangs`, default `ko`), one after another — see `broadcastPhrases`.
 * This is deliberately independent of the on-screen display language: the UI
 * may be narrowed to ko/en while the spoken alarm still cycles the full 협약
 * kiosk set + 태국어·인도네시아어 = 8개국어). The imperative `speak()` replay and
 * the display-facing `lang` field are unchanged.
 *
 * Silent-failure policy (2026-08-31): a broadcast that never reaches a speaker
 * must NEVER look like a successful one. Every failing leg — a Web Speech
 * `onerror`, a missing local voice with the server fallback off, an edge-tts
 * request/playback failure — records `lastFailure` on the store and warns on the
 * console, and any successful utterance clears the record for that language.
 * The TopBar voice button renders that state so "6개국어 방송했다" and
 * "2개만 나갔다" can no longer be confused.
 *
 * Dedupe: every alert id is spoken at most once for the tab lifetime, tracked
 * in a module-level set shared across all mounts so the passive announcer and
 * the overlay never double-speak.
 */

const supported =
  typeof window !== "undefined" && "speechSynthesis" in window;

interface TtsState {
  /** Master voice-alarm switch (TopBar / NOC / overlay). Default off. */
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  /** Allow the server edge-tts fallback when no local voice exists. */
  serverFallback: boolean;
  setServerFallback: (v: boolean) => void;
  /** Explicit voice language; null = follow the app UI language. */
  langOverride: Language | null;
  setLangOverride: (v: Language | null) => void;
  /**
   * Languages an automatic alarm is spoken in, in order. Independent of the
   * display/UI language — the operator picks these in Settings from the 협약
   * kiosk set (ko · en · zh · vi · th · id). Empty is coerced to `["ko"]`.
   */
  broadcastLangs: AlertLang[];
  setBroadcastLangs: (v: AlertLang[]) => void;
  /**
   * 이 계정의 **주 음성 언어**(서버 `voice_lang` 와 동기화되는 단일 값).
   * 단말을 바꿔도 따라오도록 서버에 저장하고, 항상 `broadcastLangs` 에 포함된다.
   */
  voiceLang: AlertLang;
  setVoiceLang: (v: AlertLang) => void;
  /**
   * 마지막으로 실패한 발화(언어·사유·시각). 성공하면 해당 언어 기록이 지워진다.
   * 세션 진단용이라 **persist 대상이 아니다**(아래 `partialize` 참조).
   */
  lastFailure: TtsFailure | null;
  noteFailure: (lang: AlertLang, reason: string) => void;
  clearFailure: (lang: AlertLang) => void;
}

/** 조용한 실패를 눈에 보이게 만드는 기록 한 건. */
export interface TtsFailure {
  lang: AlertLang;
  /** 기계 판독용 사유 코드 — `speech_error:not-allowed`, `server_tts_failed` 등. */
  reason: string;
  /** epoch ms. */
  at: number;
}

/** Persisted settings, shared across every consumer of the hook. */
export const useTtsStore = create<TtsState>()(
  persist(
    (set) => ({
      // 기본 **ON** (2026-09-03 사용자 지시: 「기본으로 경보는 켜」).
      //
      // 경위: v1 에서 기본 ON 으로 했다가 2026-08-31 사고로 v2 에서 강제로 껐다 —
      // AlertTtsAnnouncer 가 「목록을 받았다」와 「경보가 발령됐다」를 구분하지 못해
      // 화면을 열 때마다 며칠 지난 경보 12건을 줄줄이 낭독했다.
      // 그 뒤 세 겹으로 막았다: **세션 기준선**(첫 응답은 통째로 흡수하고 침묵) +
      // **틱당 최대 3건 상한** + **미해제만 낭독**(해제된 경보는 애초에 후보가 아니다).
      // 껐다 켜는 것을 사용자에게 맡겨 두면 **아무도 켜지 않은 콘솔은 처음부터 끝까지
      // 조용하다** — 가스 안전 제품에서 그건 기본값으로 둘 상태가 아니다.
      // 끄는 곳: 상단바 스피커 아이콘 · 설정 > 음성 경보 방송.
      enabled: true,
      setEnabled: (enabled) => set({ enabled }),
      serverFallback: true,
      setServerFallback: (serverFallback) => set({ serverFallback }),
      langOverride: null,
      setLangOverride: (langOverride) => set({ langOverride }),
      // Default: Korean only. Operators opt into extra spoken languages in
      // Settings; order is normalised to the canonical ALERT_LANGUAGES order.
      broadcastLangs: ["ko"],
      setBroadcastLangs: (langs) =>
        set({
          broadcastLangs: ALERT_LANGUAGES.filter((l) => langs.includes(l)) as AlertLang[],
        }),
      voiceLang: "ko",
      setVoiceLang: (voiceLang) =>
        set((s) => ({
          voiceLang,
          // 주 언어는 반드시 실제로 방송되는 언어여야 한다 — 빠져 있으면 넣는다.
          broadcastLangs: s.broadcastLangs.includes(voiceLang)
            ? s.broadcastLangs
            : (ALERT_LANGUAGES.filter(
                (l) => l === voiceLang || s.broadcastLangs.includes(l),
              ) as AlertLang[]),
        })),
      lastFailure: null,
      noteFailure: (lang, reason) => {
        // 조용히 삼키지 않는다 — 콘솔과 상단바 양쪽에 남긴다.
        console.warn(`[tts] 음성 방송 실패 — ${lang}: ${reason}`);
        set({ lastFailure: { lang, reason, at: Date.now() } });
      },
      clearFailure: (lang) =>
        set((s) => (s.lastFailure?.lang === lang ? { lastFailure: null } : s)),
    }),
    {
      name: "expertgas.tts.v4",
      // v2 = v1(기본 ON 전환)의 철회 — 각 브라우저에 enabled:false 를 써 버렸다.
      // v3 = 그 철회의 재철회(2026-09-03 지시). **저장값을 그대로 두면 v2 가 꺼 놓은
      //      기존 콘솔은 영원히 조용하다** — 기본값만 바꿔서는 현장에 반영되지 않는다.
      //      그래서 한 번 강제로 켠다(이후 사용자가 끄면 그 값이 유지된다).
      //      v2 때와 대칭이며, 되살아난 낭독은 위 세 겹(기준선·상한·미해제)이 막는다.
      version: 3,
      migrate: (state: unknown, from: number) => {
        const s = (state ?? {}) as Record<string, unknown>;
        if (from < 2) return { ...s, enabled: false };
        return from < 3 ? { ...s, enabled: true } : s;
      },
      // `lastFailure` 는 이번 세션의 진단값이라 저장하지 않는다(오래된 빨간 표시 방지).
      partialize: (s) => ({
        enabled: s.enabled,
        serverFallback: s.serverFallback,
        langOverride: s.langOverride,
        broadcastLangs: s.broadcastLangs,
        voiceLang: s.voiceLang,
      }),
    },
  ),
);

/** 훅 밖(큐 드레이너 등)에서 실패/성공을 기록하기 위한 얇은 래퍼. */
function noteFailure(lang: AlertLang, reason: string) {
  useTtsStore.getState().noteFailure(lang, reason);
}
function noteSuccess(lang: AlertLang) {
  useTtsStore.getState().clearFailure(lang);
}

// Alert ids already spoken — shared across all hook instances (passive
// announcer + overlay) so a level-3 alert handled by both never double-speaks.
const spoken = new Set<number>();

/** Minimal shape we need off an alert/alert-payload to announce it. */
export interface TtsAlertLike {
  id: number;
  level: number;
  site?: number | string | null;
  gas?: string | null;
}

// ── Voice selection (Web Speech) ─────────────────────────────────────────────
let voiceCache: SpeechSynthesisVoice[] = [];
function refreshVoices() {
  if (!supported) return;
  try {
    voiceCache = window.speechSynthesis.getVoices() ?? [];
  } catch {
    voiceCache = [];
  }
}
if (supported) {
  refreshVoices();
  try {
    window.speechSynthesis.onvoiceschanged = refreshVoices;
  } catch {
    /* some engines don't fire this — first getVoices() is enough */
  }
}

function pickVoice(lang: AlertLang): SpeechSynthesisVoice | undefined {
  const base = lang.toLowerCase();
  const locale = SPEECH_LOCALE[lang].toLowerCase();
  return (
    voiceCache.find((v) => v.lang?.toLowerCase() === locale) ??
    voiceCache.find((v) => v.lang?.toLowerCase().startsWith(base))
  );
}

// ── Server edge-tts fallback ─────────────────────────────────────────────────
let fallbackAudio: HTMLAudioElement | null = null;

/** 사유 문자열 — 예외 객체에서 사람이 읽을 한 줄만 뽑는다. */
function errText(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "string") return e;
  return "unknown";
}

async function serverSpeak(text: string, lang: AlertLang) {
  try {
    const res = await api.get("/tts/speak", {
      params: { text, lang },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data as Blob);
    stopFallbackAudio();
    const audio = new Audio(url);
    fallbackAudio = audio;
    audio.addEventListener("ended", () => URL.revokeObjectURL(url), { once: true });
    await audio.play().then(
      () => noteSuccess(lang),
      (e: unknown) => {
        URL.revokeObjectURL(url);
        noteFailure(lang, `audio_play_failed:${errText(e)}`);
      },
    );
  } catch (e) {
    // 503 tts_unavailable / network failure → visual-only alarm. Never throw —
    // 하지만 **조용히 넘어가지도 않는다**: 이 언어는 방송되지 않았다.
    noteFailure(lang, `server_tts_failed:${errText(e)}`);
  }
}

/**
 * 서버 폴백 MP3만 멈춘다.
 *
 * ⛔ 여기서 네이티브 TTS 를 멈추면 안 된다. `serverSpeak()` 은 재생 **시작할 때마다** 이 함수를
 * 부르므로, 언어팩이 없어 서버로 넘어간 언어 하나가 **이미 읽고 있던 다른 언어들을 전부 끊는다**.
 * 게다가 플러그인의 `stop()` 은 콜백 맵을 비워 버려(TextToSpeech.java `requests.clear()`),
 * 끊긴 발화의 `speak()` Promise 가 **영영 resolve/reject 되지 않는다** — 성공도 실패도 기록되지 않고
 * 서버로 넘어간 언어만 「성공」으로 남아, 소리는 조각만 났는데 화면은 8개국어 전부 정상이 된다.
 * 사용자가 명시적으로 멈춘 경우(`stopBroadcast()` · 음성 끄기)에만 네이티브를 멈춘다.
 */
function stopFallbackAudio() {
  if (fallbackAudio) {
    try {
      fallbackAudio.pause();
    } catch {
      /* ignore */
    }
    fallbackAudio = null;
  }
}

/**
 * Speak `text` in `lang`: Web Speech if a local voice exists, else the server
 * edge-tts fallback (when allowed). Never throws.
 *
 * 네이티브에서는 Web Speech 대신 OS TTS 엔진이 1단계다(그 아래 폴백은 동일).
 */
function speakText(text: string, lang: AlertLang, serverFallback: boolean) {
  void speakTextAsync(text, lang, serverFallback);
}

/**
 * `speakText` 의 await 가능한 형태 — **발화가 끝날 때까지** 기다린다.
 * 순차 테스트처럼 「하나 끝나고 다음」이 필요한 곳에서 쓴다.
 * (경보 자동 발화는 별도의 FIFO 큐가 담당하므로 이 함수를 쓰지 않는다.)
 */
async function speakTextAsync(text: string, lang: AlertLang, serverFallback: boolean) {
  if (isNativeTTS()) {
    const r = await nativeSpeak(text, lang);
    if (r.status === "ok") {
      noteSuccess(lang);
      return;
    }
    // 실패는 조용히 삼키지 않는다. 언어팩이 없어 건너뛴 경우(unsupported)는
    // 서버가 대신 읽어 주면 방송이 나간 것이므로 아직 실패가 아니다.
    if (r.status === "error") noteFailure(lang, `native_tts_failed:${r.reason}`);
    if (serverFallback) {
      await serverSpeak(text, lang);
    } else if (r.status === "unsupported") {
      noteFailure(lang, "no_local_voice_and_fallback_off");
    }
    return;
  }
  if (supported) {
    const voice = pickVoice(lang);
    // A voice exists (or none are enumerated yet → best-effort attempt).
    if (voice || voiceCache.length === 0) {
      try {
        const u = new SpeechSynthesisUtterance(text);
        u.lang = SPEECH_LOCALE[lang];
        if (voice) u.voice = voice;
        u.rate = 1;
        u.pitch = 1;
        await new Promise<void>((done) => {
          let settled = false;
          const finish = () => {
            if (!settled) { settled = true; done(); }
          };
          u.onend = () => { noteSuccess(lang); finish(); };
          u.onerror = (ev) => {
            const err = (ev as SpeechSynthesisErrorEvent).error ?? "unknown";
            // 사용자가 끄거나(mute) 다음 발화가 밀어낸 것은 실패가 아니다.
            if (err !== "interrupted" && err !== "canceled")
              noteFailure(lang, `speech_error:${err}`);
            finish();
          };
          // ⛔ 반드시 settle 시킨다 — onend/onerror 가 오지 않는 브라우저가 있다.
          setTimeout(finish, Math.min(60000, Math.max(8000, text.length * 260 + 4000)));
          window.speechSynthesis.speak(u);
        });
        return;
      } catch (e) {
        noteFailure(lang, `speech_throw:${errText(e)}`);
        /* fall through to server fallback */
      }
    }
    // Voices are loaded but none match this language → server fallback.
    if (serverFallback) await serverSpeak(text, lang);
    else noteFailure(lang, "no_local_voice_and_fallback_off");
    return;
  }
  // No Web Speech at all (older Safari, kiosk) → server fallback.
  if (serverFallback) await serverSpeak(text, lang);
  else noteFailure(lang, "no_speech_engine_and_fallback_off");
}

// ── Multilingual sequential broadcast ────────────────────────────────────────
// A single global FIFO queue speaks alerts in every selected language, strictly
// in order — across languages AND across a burst of alerts (no talk-over, no
// cut-off). Web Speech and the edge-tts fallback are unified behind a per-item
// promise so mixed engines (a local ko/en voice + server-rendered vi/th/id)
// still play one at a time.
//
// Deliberately self-contained: the single-shot `speakText`/`serverSpeak`/
// `stopFallbackAudio` paths and the disable-cancel effect above are left
// untouched, so the voice-stop/mute logic (owned by another surface) stays
// conflict-free. `stopBroadcast()` clears the queue and abandons the current
// item; `broadcastEpoch` invalidates in-flight work after a stop.
interface BroadcastItem {
  text: string;
  lang: AlertLang;
  serverFallback: boolean;
}
let broadcastQueue: BroadcastItem[] = [];
let broadcastPlaying = false;
let broadcastEpoch = 0;
let broadcastAudio: HTMLAudioElement | null = null;

/** Clear the queue and abandon the current item. Queued Web Speech utterances
 *  are cancelled by the caller's `speechSynthesis.cancel()` (mute/disable). */
function stopBroadcast() {
  broadcastQueue = [];
  broadcastEpoch++;
  broadcastPlaying = false;
  // 네이티브 엔진이 읽고 있던 문장도 끊는다(웹에서는 no-op).
  nativeStopSpeaking();
  if (broadcastAudio) {
    try {
      broadcastAudio.pause();
    } catch {
      /* ignore */
    }
    broadcastAudio = null;
  }
}

/** Play one queued item, resolving when it finishes (or is abandoned by a stop).
 *  A watchdog guarantees the queue advances even if `onend`/`ended` never fire. */
function playBroadcastItem(item: BroadcastItem, epoch: number): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const guard = setTimeout(done, Math.max(4000, item.text.length * 120));
    const finish = () => {
      clearTimeout(guard);
      done();
    };
    if (epoch !== broadcastEpoch) return finish();

    /** 서버 edge-tts MP3 leg — 웹/네이티브 양쪽의 마지막 폴백(항상 finish 로 끝난다). */
    const serverLeg = async (): Promise<void> => {
      try {
        const res = await api.get("/tts/speak", {
          params: { text: item.text, lang: item.lang },
          responseType: "blob",
        });
        if (epoch !== broadcastEpoch) return finish();
        const url = URL.createObjectURL(res.data as Blob);
        const audio = new Audio(url);
        broadcastAudio = audio;
        const cleanup = (ok: boolean, reason?: string) => {
          URL.revokeObjectURL(url);
          if (broadcastAudio === audio) broadcastAudio = null;
          if (ok) noteSuccess(item.lang);
          else noteFailure(item.lang, reason ?? "audio_failed");
          finish();
        };
        audio.addEventListener("ended", () => cleanup(true), { once: true });
        audio.addEventListener("error", () => cleanup(false, "audio_error"), { once: true });
        await audio.play().catch((e: unknown) =>
          cleanup(false, `audio_play_failed:${errText(e)}`),
        );
      } catch (e) {
        noteFailure(item.lang, `server_tts_failed:${errText(e)}`);
        finish();
      }
    };

    // ── 네이티브(Capacitor): OS TTS 가 1단계, 서버 edge-tts 가 2단계 ──────────
    // ⚠️ 완료를 await 해서 FIFO 큐가 겹치지 않게 한다(웹 경로는 아래 그대로).
    if (isNativeTTS()) {
      void (async () => {
        const r = await nativeSpeak(item.text, item.lang);
        if (epoch !== broadcastEpoch) return finish();
        if (r.status === "ok") {
          noteSuccess(item.lang);
          return finish();
        }
        // 엔진이 읽다 실패한 것은 기록한다. 언어팩이 없어 건너뛴 것(unsupported)은
        // 서버가 대신 읽으면 방송이 나간 것이므로 아직 실패가 아니다.
        if (r.status === "error") noteFailure(item.lang, `native_tts_failed:${r.reason}`);
        if (!item.serverFallback) {
          if (r.status === "unsupported") {
            noteFailure(item.lang, "no_local_voice_and_fallback_off");
          }
          return finish();
        }
        await serverLeg();
      })();
      return;
    }

    if (supported) {
      const voice = pickVoice(item.lang);
      if (voice || voiceCache.length === 0) {
        try {
          const u = new SpeechSynthesisUtterance(item.text);
          u.lang = SPEECH_LOCALE[item.lang];
          if (voice) u.voice = voice;
          u.rate = 1;
          u.pitch = 1;
          u.onend = () => {
            noteSuccess(item.lang);
            finish();
          };
          u.onerror = (ev) => {
            const err = (ev as SpeechSynthesisErrorEvent).error ?? "unknown";
            // 정지/뮤트로 끊긴 건 실패가 아니다(에포크 무효화와 같은 취급).
            if (err !== "interrupted" && err !== "canceled") {
              noteFailure(item.lang, `speech_error:${err}`);
            }
            finish();
          };
          window.speechSynthesis.speak(u);
          return;
        } catch (e) {
          noteFailure(item.lang, `speech_throw:${errText(e)}`);
          /* fall through to the server fallback */
        }
      }
    }
    // No local voice for this language → edge-tts fallback (awaited).
    if (!item.serverFallback) {
      noteFailure(item.lang, "no_local_voice_and_fallback_off");
      return finish();
    }
    void serverLeg();
  });
}

/** Drain the queue one item at a time. Idempotent: a second caller is a no-op
 *  while a drain is already running. */
async function drainBroadcast(): Promise<void> {
  if (broadcastPlaying) return;
  broadcastPlaying = true;
  const epoch = broadcastEpoch;
  while (broadcastQueue.length && epoch === broadcastEpoch) {
    const item = broadcastQueue.shift() as BroadcastItem;
    await playBroadcastItem(item, epoch);
  }
  // Release only if still the current epoch; if a stop bumped it, whoever
  // started the next drain now owns the flag (prevents a double consumer).
  if (epoch === broadcastEpoch) broadcastPlaying = false;
}

/**
 * Enqueue an alert for broadcast in each selected language, in order. `buildText`
 * renders the localized phrase per language; empty selection falls back to ko.
 * Items join the global FIFO so a burst plays sequentially, never overlapping.
 */
function broadcastPhrases(
  buildText: (lang: AlertLang) => string,
  langs: AlertLang[],
  serverFallback: boolean,
): void {
  const list = langs.length ? langs : (["ko"] as AlertLang[]);
  for (const lang of list) {
    broadcastQueue.push({ text: buildText(lang), lang, serverFallback });
  }
  void drainBroadcast();
}

export interface UseAlertTTS {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  serverFallback: boolean;
  setServerFallback: (v: boolean) => void;
  langOverride: Language | null;
  setLangOverride: (v: Language | null) => void;
  /** Languages an automatic alarm is spoken in, in order (Settings-controlled). */
  broadcastLangs: AlertLang[];
  setBroadcastLangs: (v: AlertLang[]) => void;
  /** 서버(`voice_lang`)와 동기화되는 이 계정의 주 음성 언어. */
  voiceLang: AlertLang;
  setVoiceLang: (v: AlertLang) => void;
  /** 마지막 발화 실패(성공하면 해제) — 상단바 표시·진단용. */
  lastFailure: TtsFailure | null;
  /** Effective *display* voice language right now (override, else UI language). */
  lang: AlertLang;
  supported: boolean;
  /**
   * Imperatively speak an arbitrary string right now — used by the overlay's
   * "replay" button. Unlike `announce`, this does NOT gate on `enabled`
   * (an explicit user gesture always plays); it still honours the server
   * fallback + platform support.
   */
  speak: (text: string, lang?: AlertLang) => void;
  /** `speak` 과 동일하되 발화 종료까지 기다린다(순차 테스트용). */
  speakAndWait: (text: string, lang?: AlertLang) => Promise<void>;
  /**
   * Announce a batch of alerts. Only level >= 2 and not-yet-spoken alerts are
   * read aloud; everything else is silently marked as seen so a later level
   * bump on the same id still won't re-trigger.
   */
  announce: (alerts: TtsAlertLike[], siteName?: (id: number | string) => string) => void;
  /**
   * Speak a single alert (used by the critical-alarm overlay). Shares the
   * dedupe set with `announce`, so it never double-speaks a passive one.
   */
  announceOne: (alert: TtsAlertLike, siteName?: (id: number | string) => string) => void;
  /** Build the localized spoken phrase for an alert (no audio). */
  phrase: (level: number, gas?: string | null, site?: string | null) => string;
}

function clampLevel(n: number): Level {
  if (n >= 3) return 3;
  if (n === 2) return 2;
  if (n === 1) return 1;
  return 0;
}

export function useAlertTTS(): UseAlertTTS {
  const enabled = useTtsStore((s) => s.enabled);
  const setEnabled = useTtsStore((s) => s.setEnabled);
  const serverFallback = useTtsStore((s) => s.serverFallback);
  const setServerFallback = useTtsStore((s) => s.setServerFallback);
  const langOverride = useTtsStore((s) => s.langOverride);
  const setLangOverride = useTtsStore((s) => s.setLangOverride);
  const broadcastLangs = useTtsStore((s) => s.broadcastLangs);
  const setBroadcastLangs = useTtsStore((s) => s.setBroadcastLangs);
  const voiceLang = useTtsStore((s) => s.voiceLang);
  const setVoiceLangRaw = useTtsStore((s) => s.setVoiceLang);
  const lastFailure = useTtsStore((s) => s.lastFailure);

  // Resolve the effective voice language: explicit override, else UI language.
  const lang: AlertLang = langOverride ?? toAlertLang(currentLanguage());

  // Keep latest values in refs so `announce`/`speak` stay referentially stable
  // (safe as effect deps) yet always read current settings.
  const ref = useRef({ enabled, serverFallback, lang, broadcastLangs });
  ref.current = { enabled, serverFallback, lang, broadcastLangs };

  const phrase = useCallback(
    (level: number, gas?: string | null, site?: string | null) =>
      buildAlertPhrase({ lang: ref.current.lang, level: clampLevel(level), gas, site }),
    [],
  );

  const speak = useCallback((text: string, spokenLang?: AlertLang) => {
    // Imperative (explicit gesture) — no `enabled` gate; see interface doc.
    const { serverFallback: sf, lang: l } = ref.current;
    speakText(text, spokenLang ?? l, sf);
  }, []);

  /**
   * `speak` 과 같지만 **발화가 끝날 때까지 기다린다**.
   * 여러 언어를 순서대로 들려주는 테스트(설정 > 8개국어 순차 재생)에 쓴다 —
   * 기다리지 않고 연달아 던지면 8개가 겹쳐 재생돼 무엇이 나온 건지 알 수 없다.
   */
  const speakAndWait = useCallback(async (text: string, spokenLang?: AlertLang) => {
    const { serverFallback: sf, lang: l } = ref.current;
    await speakTextAsync(text, spokenLang ?? l, sf);
  }, []);

  const speakAlert = useCallback(
    (a: TtsAlertLike, siteName?: (id: number | string) => string) => {
      const { enabled: on, serverFallback: sf, broadcastLangs: langs } = ref.current;
      if (!on) return;
      const id = a.site ?? "";
      const site = siteName && id !== "" ? siteName(id) : undefined;
      const level = clampLevel(a.level);
      // Broadcast the SAME alert in every operator-selected language, in order.
      broadcastPhrases(
        (bl) => buildAlertPhrase({ lang: bl, level, gas: a.gas, site }),
        langs,
        sf,
      );
    },
    [],
  );

  const announceOne = useCallback(
    (a: TtsAlertLike, siteName?: (id: number | string) => string) => {
      if (!a || a.id == null || spoken.has(a.id)) return;
      spoken.add(a.id);
      if (a.level < 2) return;
      speakAlert(a, siteName);
    },
    [speakAlert],
  );

  const announce = useCallback(
    (alerts: TtsAlertLike[], siteName?: (id: number | string) => string) => {
      const fresh = alerts.filter((a) => a && a.id != null && !spoken.has(a.id));
      for (const a of fresh) {
        spoken.add(a.id);
        if (a.level < 2) continue;
        speakAlert(a, siteName);
      }
    },
    [speakAlert],
  );

  // Stop any queued speech + fallback audio when the user turns TTS off.
  useEffect(() => {
    if (!enabled) {
      if (supported) {
        try {
          window.speechSynthesis?.cancel();
        } catch {
          /* ignore */
        }
      }
      stopFallbackAudio();
    }
  }, [enabled]);

  // Additionally halt an in-flight *multilingual* broadcast when voice is turned
  // off. The effect above cancels queued Web Speech; this stops the server-audio
  // leg the broadcaster owns. Kept as a separate effect so the single-shot
  // voice-stop/mute path stays untouched.
  useEffect(() => {
    if (!enabled) stopBroadcast();
  }, [enabled]);

  return {
    enabled,
    setEnabled,
    serverFallback,
    setServerFallback,
    langOverride,
    setLangOverride,
    broadcastLangs,
    setBroadcastLangs,
    voiceLang,
    setVoiceLang: setVoiceLangRaw,
    lastFailure,
    lang,
    // 네이티브에서는 Web Speech 유무와 무관하게 OS TTS·서버 폴백으로 소리가 난다.
    // (웹에서는 `isNativeTTS()` 가 false 라 기존 값 그대로다.)
    supported: supported || isNativeTTS(),
    speak,
    speakAndWait,
    announce,
    announceOne,
    phrase,
  };
}


// ── 개인 음성 언어 서버 동기화 (FE-5) ─────────────────────────────────────────
// 방송 언어가 브라우저 localStorage 에만 있으면 단말을 바꾸는 순간 초기화된다.
// 백엔드가 사용자 프로필에 노출하는 `voice_lang`(주 언어 문자열 하나)과 store 를
// 양방향으로 잇되, **서버에 그 필드가 아직 없어도 조용히 무시**한다:
// 값이 없으면 기존 localStorage 설정을 그대로 쓰고, PATCH 실패는 삼킨다.
//
// `lastPushed` = 「서버가 알고 있다고 믿는 값」. 첫 렌더에서 로컬값으로 baseline 을
// 잡으므로 부팅만으로 서버에 쓰지 않고, 사용자가 바꾼 뒤에만 PATCH 가 나간다.
let lastPushed: AlertLang | null = null;

/** 서버 프로필에서 읽은 값을 AlertLang 으로 정규화. 인식 불가 → null. */
function parseVoiceLang(raw: unknown): AlertLang | null {
  if (typeof raw !== "string") return null;
  // 백엔드가 콤마 목록/BCP-47("ja-JP")을 줄 수도 있으니 첫 토큰의 기본 언어만 본다.
  const base = raw.trim().toLowerCase().split(",")[0]?.split("-")[0] ?? "";
  return (ALERT_LANGUAGES as readonly string[]).includes(base) ? (base as AlertLang) : null;
}

/**
 * 로그인/부팅 시 서버 `voice_lang` → store, 이후 사용자가 바꾸면 store → PATCH.
 * AppShell 에서 한 번만 마운트한다.
 */
export function useVoiceLangSync(): void {
  const user = useAuth((s) => s.user);
  const authed = useAuth((s) => !!s.access);
  const voiceLang = useTtsStore((s) => s.voiceLang);

  // 1) 서버 → store. 필드가 없거나 값이 이상하면 아무것도 하지 않는다.
  useEffect(() => {
    if (!user) return;
    const fromServer = parseVoiceLang((user as { voice_lang?: unknown }).voice_lang);
    if (!fromServer) return;
    lastPushed = fromServer;
    if (useTtsStore.getState().voiceLang !== fromServer) {
      useTtsStore.getState().setVoiceLang(fromServer);
    }
  }, [user]);

  // 2) store → 서버. 첫 실행은 baseline 만 잡고 쓰지 않는다.
  useEffect(() => {
    if (!authed) return;
    if (lastPushed === null) {
      lastPushed = voiceLang;
      return;
    }
    if (lastPushed === voiceLang) return;
    lastPushed = voiceLang;
    void authApi.updateMe({ voice_lang: voiceLang }).catch(() => {
      // 서버에 아직 `voice_lang` 이 없을 수 있다 → 조용히 무시하고 로컬 설정 유지.
    });
  }, [authed, voiceLang]);
}
